import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { apiRequest, queryClient } from "../lib/api";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  } from "../lib/utils";
import { ArrowLeft, Send, Wallet, LogIn, Copy } from "lucide-react";
import { Link } from "wouter";

interface UserProfile {
  user: Record<string, any>;
  bots: Array<Record<string, any>>;
  payments: Array<Record<string, any>>;
  transactions: Array<Record<string, any>>;
}

export default function UserProfilePage() {
  const [, params] = useRoute("/users/:id");
  const userId = params?.id;

  const [activeTab, setActiveTab] = useState<"info" | "payments" | "bots">(
    "info"
  );
  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceReason, setBalanceReason] = useState("");
  const [showBalanceDialog, setShowBalanceDialog] = useState(false);
  const [telegramMessage, setTelegramMessage] = useState("");
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [impersonateUrl, setImpersonateUrl] = useState<string | null>(null);
  const [impersonateCopied, setImpersonateCopied] = useState(false);

  const { data, isLoading } = useQuery<UserProfile>({
    queryKey: [`/api/admin/users/${userId}`],
    enabled: !!userId,
  });

  const adjustBalanceMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/admin/users/${userId}/adjust-balance`, {
        amount: Number(balanceAmount),
        reason: balanceReason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/admin/users/${userId}`] });
      setShowBalanceDialog(false);
      setBalanceAmount("");
      setBalanceReason("");
    },
  });

  const impersonateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/admin/users/${userId}/impersonate-link`, {});
      return (await res.json()) as { url: string; expiresAt: string };
    },
    onSuccess: (data) => {
      setImpersonateUrl(data.url);
      setImpersonateCopied(false);
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/admin/users/${userId}/send-message`, {
        message: telegramMessage,
      });
    },
    onSuccess: () => {
      setShowMessageDialog(false);
      setTelegramMessage("");
    },
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded w-48 animate-pulse" />
        <div className="h-64 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  const { user, bots, payments, transactions } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link href="/users">
          <button className="p-2 rounded-md hover:bg-muted transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">{user.email}</h1>
          <p className="text-sm text-muted-foreground">
            {[user.first_name, user.last_name].filter(Boolean).join(" ")}
            {user.telegram_username && ` · @${user.telegram_username}`}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Баланс</div>
          <div className="text-xl font-bold">
            {formatCurrency(Number(user.balance))}
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Потрачено</div>
          <div className="text-xl font-bold">
            {formatCurrency(Number(user.total_spent))}
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Боты</div>
          <div className="text-xl font-bold">{user.bot_count}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Регистрация</div>
          <div className="text-xl font-bold">
            {formatDate(user.created_at)}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => setShowBalanceDialog(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Wallet className="w-4 h-4" /> Корректировка баланса
        </button>
        {user.telegram_chat_id && (
          <button
            onClick={() => setShowMessageDialog(true)}
            className="flex items-center gap-2 px-4 py-2 border border-input rounded-md text-sm font-medium hover:bg-muted transition-colors"
          >
            <Send className="w-4 h-4" /> Написать в Telegram
          </button>
        )}
        {user.telegram_username && (
          <a
            href={`https://t.me/${user.telegram_username}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-4 py-2 border border-input rounded-md text-sm font-medium hover:bg-muted transition-colors"
          >
            Открыть @{user.telegram_username}
          </a>
        )}
        <button
          onClick={() => impersonateMutation.mutate()}
          disabled={impersonateMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 border border-input rounded-md text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
        >
          <LogIn className="w-4 h-4" />
          {impersonateMutation.isPending ? "Генерация..." : "Войти как пользователь"}
        </button>
      </div>

      {impersonateUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold">Одноразовая ссылка входа</h2>
            <p className="text-sm text-muted-foreground">
              Ссылка действительна 15 минут и сгорает после первого открытия. Откройте в приватном окне, чтобы не потерять свою админ-сессию.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={impersonateUrl}
                className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-xs font-mono"
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(impersonateUrl);
                    setImpersonateCopied(true);
                  } catch {
                    setImpersonateCopied(false);
                  }
                }}
                className="p-2 rounded-md border border-input hover:bg-muted"
                title="Скопировать"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {impersonateCopied && (
              <div className="text-xs text-green-600">Скопировано</div>
            )}
            <div className="flex justify-end gap-2">
              <a
                href={impersonateUrl}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium"
              >
                Открыть в новой вкладке
              </a>
              <button
                onClick={() => setImpersonateUrl(null)}
                className="px-4 py-2 border border-input rounded-md text-sm hover:bg-muted"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Balance Adjustment Dialog */}
      {showBalanceDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-sm space-y-4">
            <h2 className="text-lg font-semibold">Корректировка баланса</h2>
            <div>
              <label className="block text-sm font-medium mb-1">
                Сумма (положительная — начисление, отрицательная — списание)
              </label>
              <input
                type="number"
                value={balanceAmount}
                onChange={(e) => setBalanceAmount(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
                placeholder="500 или -200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Причина</label>
              <input
                type="text"
                value={balanceReason}
                onChange={(e) => setBalanceReason(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
                placeholder="Бонус / Возврат / и т.д."
              />
            </div>
            {adjustBalanceMutation.error && (
              <div className="text-sm text-destructive">
                {(adjustBalanceMutation.error as Error).message}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowBalanceDialog(false)}
                className="px-4 py-2 border border-input rounded-md text-sm hover:bg-muted"
              >
                Отмена
              </button>
              <button
                onClick={() => adjustBalanceMutation.mutate()}
                disabled={
                  !balanceAmount || !balanceReason || adjustBalanceMutation.isPending
                }
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
              >
                {adjustBalanceMutation.isPending ? "Сохранение..." : "Подтвердить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Telegram Message Dialog */}
      {showMessageDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-sm space-y-4">
            <h2 className="text-lg font-semibold">Сообщение в Telegram</h2>
            <textarea
              value={telegramMessage}
              onChange={(e) => setTelegramMessage(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm min-h-[100px]"
              placeholder="Текст сообщения..."
            />
            {sendMessageMutation.error && (
              <div className="text-sm text-destructive">
                {(sendMessageMutation.error as Error).message}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowMessageDialog(false)}
                className="px-4 py-2 border border-input rounded-md text-sm hover:bg-muted"
              >
                Отмена
              </button>
              <button
                onClick={() => sendMessageMutation.mutate()}
                disabled={!telegramMessage || sendMessageMutation.isPending}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
              >
                {sendMessageMutation.isPending ? "Отправка..." : "Отправить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-6">
          {(["info", "payments", "bots"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab === "info"
                ? "Инфо и транзакции"
                : tab === "payments"
                  ? "Платежи"
                  : "Боты"}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "info" && (
        <div className="space-y-4">
          <h3 className="font-semibold">Информация</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Телефон:</span>{" "}
              {user.phone || "—"}
            </div>
            <div>
              <span className="text-muted-foreground">Telegram ID:</span>{" "}
              {user.telegram_chat_id || "—"}
            </div>
            <div>
              <span className="text-muted-foreground">Бонусный баланс:</span>{" "}
              {formatCurrency(Number(user.bonus_balance))}
            </div>
            <div>
              <span className="text-muted-foreground">Создан:</span>{" "}
              {formatDateTime(user.created_at)}
            </div>
          </div>

          <h3 className="font-semibold mt-6">Последние транзакции</h3>
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                    Тип
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                    Сумма
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                    Описание
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                    Дата
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {transactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-4 text-center text-muted-foreground text-sm"
                    >
                      Нет транзакций
                    </td>
                  </tr>
                ) : (
                  transactions.map((t: any) => (
                    <tr key={t.id}>
                      <td className="px-4 py-2 text-sm">{t.type}</td>
                      <td className="px-4 py-2 text-sm font-mono">
                        {formatCurrency(Number(t.amount))}
                      </td>
                      <td className="px-4 py-2 text-sm text-muted-foreground">
                        {t.description}
                      </td>
                      <td className="px-4 py-2 text-sm text-muted-foreground">
                        {formatDateTime(t.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "payments" && (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Сумма
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Статус
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Оплачен
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Создан
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {payments.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-4 text-center text-muted-foreground text-sm"
                  >
                    Нет платежей
                  </td>
                </tr>
              ) : (
                payments.map((p: any) => (
                  <tr key={p.id}>
                    <td className="px-4 py-2 text-sm font-mono">
                      {formatCurrency(Number(p.amount))}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          p.status === "succeeded"
                            ? "bg-green-100 text-green-700"
                            : p.status === "pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-red-100 text-red-700"
                        }`}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {p.paid_at ? formatDateTime(p.paid_at) : "—"}
                    </td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {formatDateTime(p.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "bots" && (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Название
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Модель
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Статус
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Чаты
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Создан
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {bots.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-4 text-center text-muted-foreground text-sm"
                  >
                    Нет AI-сотрудников
                  </td>
                </tr>
              ) : (
                bots.map((b: any) => (
                  <tr key={b.id}>
                    <td className="px-4 py-2 text-sm font-medium">{b.name}</td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {b.model}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          b.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {b.is_active ? "Активен" : "Неактивен"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">{b.chat_count}</td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {formatDate(b.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
